/**
 * @fileoverview Comprehensive FAQ page with expandable sections
 */

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronRight, HelpCircle, Gamepad2, Users, Settings, Share2, Shield } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
  category: 'general' | 'games' | 'account' | 'technical' | 'safety';
}

const FAQ: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0])); // First item expanded by default

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const faqData: FAQItem[] = [
    // Top Questions (priority)
    {
      question: 'What is Spinner Games !! app?',
      answer: (
        <span>
          Spinner Games !! is a free, interactive spinner app that helps you make decisions, play games, and have fun with friends. It includes Would You Rather?, Which One Are You?, Truth or Dare, and Trivia games to bring family and friends together. The app also features custom spinner options so you can create and play your own games. Please go through our{' '}
          <a href="https://spinnergames.fun/blogs" className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors" target="_blank" rel="noopener noreferrer">Blog</a>{' '}for educational and fun ideas.
        </span>
      ),
      category: 'general'
    },
    {
      question: 'Can I change the language?',
      answer: (
        <span>
          Yes! Click on the language selector in the top menu to choose from our supported languages. You can also translate the app, help, or blogs easily with your browser. See{' '}
          <a href="https://spinnergames.fun/blogs/translate-in-browser/" className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors" target="_blank" rel="noopener noreferrer">Blog</a> for a step-by-step guide.
        </span>
      ),
      category: 'technical'
    },
    {
      question: 'Can I mirror my spinner app to a TV or a projector screen?',
      answer: (
        <span>
          Yes. On Android, use Smart View (or Cast) to mirror to a Cast-enabled TV. On Apple devices, use the built-in Screen Mirroring (AirPlay). You can also use Chromecast with your TV, and cast from Chrome on desktop or the Google Home app. See{' '}
          <a href="https://spinnergames.fun/blogs/casting-and-mirroring/" className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors" target="_blank" rel="noopener noreferrer">Blog</a> for full instructions and options.
        </span>
      ),
      category: 'technical'
    },

    // General Questions
    {
      question: "Is the app completely free to use?",
      answer: "Yes! This app with all features is completely free. You can create custom spinners, play all game types, and share spinner without any cost. Login has addtional benefits.",
      category: "general"
    },
    {
      question: "Do I need to create an account?",
      answer: "No account is required to use the basic features. However, creating an account benefits you to save your favorite spinners, results, and access them from any device, and get personalized recommendations. No login, data will be stored locally and will be lost if you clear your browser cache.",
      category: "account"
    },
    {
      question: "What devices are supported?",
      answer: "Our app works on all devices - smartphones, tablets, laptops, and desktop computers. It's a web-based application, so you just need a modern browser and internet connection.",
      category: "technical"
    },
    {
      question: "Can I use the app offline?",
      answer: "Currently, the app requires an internet connection to load content and save your spinners. We're working on offline functionality for future updates.",
      category: "technical"
    },

    // Game-Specific Questions
    {
      question: "How do I create a custom spinner?",
      answer: "Click on the 'Participants' section and type in names or options. Use the + button to add more entries, alternatively you can bulk load participants with Load button. Select your favorite Spinner Styles. You can also toggle audio, dark mode to have personalized touch.",
      category: "games"
    },
    {
      question: "Can I save my custom spinners?",
      answer: "Yes! If you're logged in, you can save your spinner with Save button. You can just save for later use, or also share or send to friends with social media or direct link. You can access your saved spinners any time from the Open button in menu. You can save maximum 30 spinners.",
      category: "games"
    },
    {
      question: "How many participants can I add?",
      answer: "You can add up to 36 participants or options in a single spinner. This should be more than enough for most use cases like parties, meetings, or decision-making.",
      category: "games"
    },
    {
      question: "Are the game questions appropriate for all ages?",
      answer: "Yes! All our content is kids and family-friendly and suitable for all ages. We have content filters and moderation systems to ensure safety. You can also enable additional content filtering in settings.",
      category: "safety"
    },
    {
      question: "Can I report inappropriate content?",
      answer: "Absolutely! If you encounter any inappropriate content, use the 'Report' button on any question or contact us through our Contact page. We take all reports seriously and review them promptly.",
      category: "safety"
    },

    // Technical Questions
    {
      question: "Why isn't the spinner working?",
      answer: "Try refreshing the page or clearing your browser cache. Make sure you have a stable internet connection. If the problem persists, contact our support team with details about your device and browser.",
      category: "technical"
    },
    // (moved language question to top)
    {
      question: "How do I share my spinner with friends?",
      answer: "After creating your spinner, click the 'Share' in menu. Provide Spinner Name (optional), and share it either in social media or copy direct link and send it to friends via text, email etc. They can open the link to see your exact spinner configuration.",
      category: "games"
    },
    {
      question: "Are my personal details safe?",
      answer: "Yes, we take privacy seriously. We only collect necessary information and never share your personal data with third parties. Read our Privacy Policy for complete details on how we protect your information.",
      category: "safety"
    }
  ];

  const categories = [
    { key: 'general', label: 'General', icon: <HelpCircle className="w-5 h-5" /> },
    { key: 'games', label: 'Games & Features', icon: <Gamepad2 className="w-5 h-5" /> },
    { key: 'account', label: 'Account & Settings', icon: <Settings className="w-5 h-5" /> },
    { key: 'technical', label: 'Technical Support', icon: <Settings className="w-5 h-5" /> },
    { key: 'safety', label: 'Safety & Privacy', icon: <Shield className="w-5 h-5" /> }
  ];

  // Show all questions (category filter removed)
  const filteredFAQ = faqData;

  return (
    <HelpPageLayout>
      <Helmet>
        <title>FAQ - Spinner Games !!</title>
        <meta name="description" content="Find answers to frequently asked questions about Spinner Games !! Learn how to use our spinner app, troubleshoot issues, and get support." />
                  <meta property="og:title" content="FAQ - Spinner Games !!" />
        <meta property="og:description" content="Frequently asked questions and answers about our interactive spinner app." />
        <link rel="canonical" href="https://spinnergames.fun/help/faq" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": typeof faq.answer === 'string' ? faq.answer : faq.question + " - See our FAQ page for detailed answer."
            }
          }))
        })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Find answers to common questions about our spinner app
          </p>
        </div>

        {/* Category filter removed as requested */}

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQ.map((item, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                  {item.question}
                </h3>
                {expandedItems.has(index) ? (
                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {expandedItems.has(index) && (
                <div className="px-6 pb-4">
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch">
            <a
              href="/help/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
            >
              Contact Support
            </a>
            <a
              href="/help"
              className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors w-full sm:w-auto"
            >
              Back to Help Center
            </a>
          </div>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default FAQ; 